import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { UserProvider } from "./context/userContext";
import { ThemeProvider } from "./context/themeContext";
import { DrawerProvider } from "./context/drawerContext";
import { HeaderProvider } from "./context/headerContext";
import { CartProvider } from "./context/cartContext";
import Root from "./routes/root";
import Checkout from "./routes/stripe/checkout";
import CheckoutSuccess from "./routes/stripe/success";
import CheckoutError from "./routes/stripe/error";
import Incidents from "./routes/incidents";
import Incident from "./routes/incidents/single";
import IncidentHistory from "./routes/incidents/history";
import IncidentHistorySingle from "./routes/incidents/history/single";
import Activities from "./routes/log";
import Activity from "./routes/log/single";
import Milestones from "./routes/milestones";
import Milestone from "./routes/milestones/single";
import Venues from "./routes/venues";
import Venue from "./routes/venues/single";
import VenueParticipants from "./routes/venues/single/participants";
import VenueZone from "./routes/venues/single/zones/single";
import Bookings from "./routes/bookings";
import BookingsScan from "./routes/bookings/scan";
import AssignBandScan from "./routes/bookings/assign-band-scan";
import ParticipantBooking from "./routes/bookings/single";
import ParticipantBookingProfile from "./routes/bookings/single/participant";
import Scan from "./routes/scan";
import ErrorScan from "./routes/scan/error";
import NormalScan from "./routes/scan/normal-scan";
import EmergencyScan from "./routes/scan/emergency-scan";
import Settings from "./routes/settings";
import Weather from "./routes/settings/weather";
import Log from "./routes/staff-log";
import Account from "./routes/account";
import UpgradeSubscription from "./routes/upgrade";
import Home from "./routes/home";
import ParticipantOnboarding from "./routes/participant-onboarding";
import StaffOnboarding from "./routes/staff-onboarding";
import Login from "./routes/login";
import PasswordReset from "./routes/password-reset";
import DrawerWrapper from "./routes/drawer-wrapper";
import NotFound from "./routes/not-found";
import AddBand from "./routes/add-band";
import AddBandScan from "./routes/add-band/scan";
import AddBandComplete from "./routes/add-band/complete";
//import BandProfile from "./routes/band/profile";
import ParticipantProfile from "./routes/participant";
import ParticipantEditProfile from "./routes/participant/edit";
import ParticipantBands from "./routes/participant/bands";
import BandPage from "./routes/participant/bands/single";
import GuestBandsDirectory from "./routes/guest-bands";
import ScanGuestBand from "./routes/guest-bands/scan";
import ActivateGuestBand from "./routes/guest-bands/activate";
import "@fontsource/poppins";
import "./index.css";
import GuestBandComplete from "./routes/guest-bands/activate/complete";
import Shop from "./routes/shop";
import MyParticipants from "./routes/shop/my-participants";
// import MyBands from "./routes/shop/my-participants/my-bands";
import AddNewParticipant from "./routes/shop/my-participants/add-new-participant";
import ProductSummary from "./routes/shop/product-summary";
import Cart from './routes/shop/cart';
import ShopCheckout from './routes/shop/checkout';
import ShopCheckoutSuccess from "./routes/shop/checkout/success";
import ShopCheckoutFailure from "./routes/shop/checkout/failure";
import VenueShop from "./routes/venue-shop";
import { registerSW } from 'virtual:pwa-register'
import * as Sentry from "@sentry/react";
import { CookiesProvider } from "react-cookie";
import AccountRedeemBand from "./routes/account/redeem";
import AccountRedeemBandSuccess from "./routes/account/redeem-success";
// import { LogErrors } from "./utils/LogErrors";

Sentry.init({
  dsn: "https://cfcb659147b56d27c881b650bdb5827b@o4508993807581184.ingest.de.sentry.io/4508998197248081",
  integrations: [
    // Sentry.replayIntegration(),
    Sentry.browserTracingIntegration()
  ],
  // Session Replay
  replaysSessionSampleRate: 0, // Video session recording is disabled by setting the sample rate to 0.
  replaysOnErrorSampleRate: 0, // Video session recording on errors is also disabled by setting the sample rate to 0.
  // Performance Tracing
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    "https://api.sportsguardian.com/api/",
    "https://api-dev.sportsguardian.com/api/",
    "localhost", // For local API requests
  ],
  // Function Performance Tracing
  profilesSampleRate: 1.0
});

const UPDATE_CHECK_INTERVAL = 20000; // 20sec

registerSW({ immediate: true,
  onRegisteredSW(swUrl, r) {
    // LogErrors(user, logout, updateTokens, `Refresh Checker: service worker at: ${swUrl}`);

    r && setInterval(async () => {
        // LogErrors(user, logout, updateTokens, 'Refresh Checker: checking for update');
        await r.update();
    }, UPDATE_CHECK_INTERVAL);
  },
 });

// Main Routes for project
const router = createBrowserRouter([
  {
    path: "/",
    element: <DrawerWrapper />,
    children: [
      {
        path: "/",
        element: <Root />,
        errorElement: <NotFound />,
        children: [
          {
            index: true,
            element: <Home />,
          },
          {
            path: "incidents",
            element: <Incidents />,
          },
          {
            path: "incidents/:incidentId",
            element: <Incident />,
          },
          {
            path: "add-band/scan",
            element: <AddBandScan />,
          },
          {
            path: "add-band/complete",
            element: <AddBandComplete />,
          },
          {
            path: "add-band/:step",
            element: <AddBand />,
          },
          {
            path: "participant/:participantId",
            element: <ParticipantProfile />,
          },
          {
            path: "participant/:participantId/edit",
            element: <ParticipantEditProfile />,
          },
          {
            path: "participant/:participantId/bands",
            element: <ParticipantBands />,
          },
          {
            path: "participant/:participantId/bands/:bandId",
            element: <BandPage />,
          },
          {
            path: "participant/:participantId/incident-history",
            element: <IncidentHistory />,
          },
          {
            path: "participant/:participantId/incident-history/:incidentId",
            element: <IncidentHistorySingle />,
          },
          // {
          //   path: "band/:bandId",
          //   element: <Band />,
          // },
          // {
          //   path: "band/:bandId/profile",
          //   element: <ParticipantProfile />,
          // },
          {
            path: "log",
            element: <Activities />,
          },
          {
            path: "activity/:activityId",
            element: <Activity />,
          },
          {
            path: "milestones",
            element: <Milestones />,
          },
          {
            path: "milestones/:milestoneId",
            element: <Milestone />,
          },
          {
            path: "venues",
            element: <Venues />,
          },
          {
            path: "venues/:venueId",
            element: <Venue />,
          },
          {
            path: "venues/:venueId/participants",
            element: <VenueParticipants />,
          },
          {
            path: "venues/:venueId/zones/:zoneId",
            element: <VenueZone />,
          },
          {
            path: "bookings",
            element: <Bookings />,
          },
          {
            path: "bookings/scan",
            element: <BookingsScan />,
          },
          {
            path: "bookings/assign-band/scan",
            element: <AssignBandScan />,
          },
          {
            path: "bookings/:bookingId/participant/:participantId",
            element: <ParticipantBookingProfile />,
          },
          {
            path: "bookings/:bookingId",
            element: <ParticipantBooking />,
          },
          {
            path: "scan",
            element: <Scan />,
          },
          {
            path: "scan/normal",
            element: <NormalScan />,
          },
          {
            path: "scan/emergency",
            element: <EmergencyScan />,
          },
          {
            path: "scan/error",
            element: <ErrorScan />,
          },
          {
            path: "staff-log",
            element: <Log />,
          },
          {
            path: "settings",
            element: <Settings />,
          },
          {
            path: "weather/:venueId",
            element: <Weather />,
          },
          {
            path: "upgrade",
            element: <UpgradeSubscription />,
          },
          {
            path: "account",
            element: <Account />,
          },
          {
            path: "account/redeem",
            element: <AccountRedeemBand />,
          },
          {
            path: "account/redeem-success",
            element: <AccountRedeemBandSuccess />,
          },
          {
            path: "participant-onboarding",
            element: <ParticipantOnboarding />,
          },
          {
            path: "staff-onboarding",
            element: <StaffOnboarding />,
          },
          {
            path: "checkout",
            element: <Checkout />,
          },
          {
            path: "checkout/error",
            element: <CheckoutError />,
          },
          {
            path: "checkout/success",
            element: <CheckoutSuccess />,
          },
          {
            path: "guest-bands",
            element: <GuestBandsDirectory />,
          },
          {
            path: "guest-bands/scan",
            element: <ScanGuestBand />,
          },
          {
            path: "guest-bands/:bandId/activate/:step",
            element: <ActivateGuestBand />,
          },
          {
            path: "guest-bands/:bandId/activate/complete",
            element: <GuestBandComplete />,
          },
          {
            path: "shop",
            element: <Shop />,
          },
          {
            path: "shop/my-participants",
            element: <MyParticipants />,
          },
          // {
          //   path: "shop/my-participants/my-bands",
          //   element: <MyBands />,
          // },
          {
            path: "shop/my-participants/add-new-participant",
            element: <AddNewParticipant />,
          },
          {
            path: "shop/product-summary",
            element: <ProductSummary />,
          },
          {
            path: "cart",
            element: <Cart />
          },
          {
            path: "shop/checkout",
            element: <ShopCheckout />,
          },
          {
            path: "shop/checkout/success",
            element: <ShopCheckoutSuccess />,
          },
          {
            path: "shop/checkout/failure",
            element: <ShopCheckoutFailure />,
          },
          {
            path: "venue-shop",
            element: <VenueShop />,
          }
        ],
      },
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "password-reset",
        element: <PasswordReset />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);

//Render the application
ReactDOM.createRoot(document.getElementById("root")).render(
    <div className="flex items-center justify-center w-full bg-white border-x-black">
      {/* The div below makes sure the  application is always contained within a mobile view using the max resolution of an iPhone 14 Pro */}
      <div className="w-full h-full max-w-[430px] max-h-[932px] bg-white">
        {/* <SplashScreen /> */}
        <CookiesProvider>
        <ThemeProvider>
        <DrawerProvider>
          <UserProvider>
            <CartProvider>
              <HeaderProvider>
                  <RouterProvider router={router} />
              </HeaderProvider>
            </CartProvider>
          </UserProvider>
          </DrawerProvider>
        </ThemeProvider>
        </CookiesProvider>
      </div>
    </div>
);
